import {
  LOCALE,
  CACHE_TYPE,
  type SDKConfig,
  type CacheConfig,
  type AfterTranslateCallback,
  type TranslateFunction,
  type GetTokenFunction,
} from '../types';

const SDK_DEFAULTS: Required<Pick<SDKConfig, 'locale' | 'chunkSize' | 'maxRetries'>> = {
  locale: LOCALE.EN,
  chunkSize: 500,
  maxRetries: 3,
};

const CACHE_DEFAULTS: CacheConfig = {
  enabled: false,
  type: CACHE_TYPE.LOCAL_STORAGE,
  expireTime: -1,
};

export class ConfigManager {
  private config: SDKConfig;
  private cacheConfig: CacheConfig;

  constructor(userConfig: SDKConfig) {
    this.config = { ...SDK_DEFAULTS, ...userConfig };

    const cacheOpt = userConfig.cache;
    if (!cacheOpt) {
      this.cacheConfig = { ...CACHE_DEFAULTS };
    } else if (cacheOpt === true) {
      this.cacheConfig = { ...CACHE_DEFAULTS, enabled: true };
    } else {
      this.cacheConfig = {
        enabled: true,
        type: cacheOpt.type ?? CACHE_DEFAULTS.type,
        expireTime: cacheOpt.expireTime ?? CACHE_DEFAULTS.expireTime,
      };
    }
  }

  get locale(): LOCALE {
    return this.config.locale ?? SDK_DEFAULTS.locale;
  }

  get getToken(): GetTokenFunction | undefined {
    return this.config.getToken;
  }

  get terminologies(): Array<{ src: string; tgt: string }> {
    return this.config.terminologies ?? [];
  }

  get examples(): Array<{ src: string; tgt: string }> {
    return this.config.examples ?? [];
  }

  get domainHint(): string | undefined {
    return this.config.domainHint;
  }

  get chunkSize(): number {
    return this.config.chunkSize ?? SDK_DEFAULTS.chunkSize;
  }

  get maxRetries(): number {
    return this.config.maxRetries ?? SDK_DEFAULTS.maxRetries;
  }

  get afterTranslate(): AfterTranslateCallback | undefined {
    return this.config.afterTranslate;
  }

  get cache(): CacheConfig {
    return this.cacheConfig;
  }

  get translate(): TranslateFunction | undefined {
    return this.config.translate;
  }

  getRaw(): Readonly<SDKConfig> {
    return this.config;
  }

  update(partial: Partial<SDKConfig>): void {
    this.config = { ...this.config, ...partial };
  }
}
